import { anthropic } from '@ai-sdk/anthropic';
import { openai } from '@ai-sdk/openai';
import type { LlmCostAccounting } from '@edgevanta/bid-tab-extract';
import type { LanguageModel } from 'ai';

import type { CategoryCandidate } from '../item-classification';
import { type ClassifierInput, classifyBidTabItems } from './classifier';

/** A line to classify — identity is description + unit. */
export interface ClassifyLine {
  description: string;
  unit: string | null;
}

/** The id resolvers the classify brain needs (pure code→id lookups). */
export interface ClassifyResolvers {
  findCategoryCandidates(items: ClassifyLine[]): Promise<CategoryCandidate[][]>;
  resolveCategoryIdsByCode(codes: string[]): Promise<Map<string, string>>;
  resolveCollectionIdsByCode(codes: string[]): Promise<Map<string, string>>;
  resolveUnitsByInput(units: string[]): Promise<Map<string, UnitResolution>>;
}

export interface UnitResolution {
  id: string;
  code: string;
}

/** The classification decided + resolved for one line. */
export interface ClassifiedLine {
  categoryId: string | null;
  categoryCode: string | null;
  isTrueLumpSum: boolean;
  liquidAcPattern: string | null;
  unitId: string | null;
  collections: Array<{
    collectionId: string;
    code: string;
    sourceUom: string;
    sourceUnitId: string | null;
    conversionFactor: number;
  }>;
}

interface ClassifyLinesOptions {
  model?: LanguageModel;
  novelModel?: LanguageModel;
  costAccounting?: LlmCostAccounting;
  /** Lets the staging path share one unit-catalog read across classified and
   *  reused lines while that read overlaps the model calls. */
  unitResolutions?: Promise<Map<string, UnitResolution>>;
}

const CLASSIFICATION_BATCH_SIZE = 400;
const CLASSIFICATION_CONCURRENCY = 2;

/**
 * The shared classify brain used by BOTH the persisted path
 * (`classifyProposalItems`) and the staging path (`enrichStaging`): retrieve
 * candidates → classify with the dbt prompt (cheap model, Sonnet tail) →
 * resolve codes/units to ids → return per-line enrichment. The attribute bundle
 * (category + lump-sum + collections) travels WHOLE off the top DOT candidate
 * (EDG-1335) — never field-by-field.
 */
export async function classifyLines(
  lines: ClassifyLine[],
  resolvers: ClassifyResolvers,
  options: ClassifyLinesOptions = {},
): Promise<ClassifiedLine[]> {
  if (lines.length === 0) {
    return [];
  }

  const { uniqueLines, uniqueIndexByLine } = deduplicateLines(lines);
  const unitResolutionsState = settle(
    options.unitResolutions ??
      resolvers.resolveUnitsByInput(lines.map((it) => it.unit ?? '')),
  );
  const candidates = await resolvers.findCategoryCandidates(uniqueLines);
  const inputs: ClassifierInput[] = uniqueLines.map((it, i) => ({
    description: it.description,
    unit: it.unit,
    hints: (candidates[i] ?? []).slice(0, 3).map((c) => ({
      code: c.attributes.code,
      name: c.name,
      similarity: c.similarity,
    })),
  }));

  const cheapModel = options.model ?? openai('gpt-4.1-mini');
  const results = await classifyInBatches(inputs, cheapModel, {
    costAccounting: options.costAccounting,
    stage: 'normalize:classification',
  });

  const novelIdx = results.flatMap((r, i) =>
    r.categoryCode === '99' ? [i] : [],
  );
  if (novelIdx.length > 0) {
    const strongModel = options.novelModel ?? anthropic('claude-sonnet-4-6');
    const novelInputs = novelIdx.flatMap((i) => {
      const input = inputs[i];
      return input ? [input] : [];
    });
    const novelResults = await classifyInBatches(novelInputs, strongModel, {
      costAccounting: options.costAccounting,
      stage: 'normalize:classification:novel',
    });
    novelIdx.forEach((idx, j) => {
      const better = novelResults[j];
      if (better && better.categoryCode !== '99') {
        results[idx] = better;
      }
    });
  }

  const attributesFor = (i: number) => candidates[i]?.[0]?.attributes ?? null;
  const collectionsFor = (
    i: number,
  ): Array<{ code: string; factor: number }> => {
    const attrs = attributesFor(i);
    if (attrs && attrs.collections.length > 0) {
      return attrs.collections;
    }
    return results[i]?.collections ?? [];
  };
  const [categoryIds, collectionIds, unitState] = await Promise.all([
    resolvers.resolveCategoryIdsByCode([
      ...new Set(results.map((r) => r.categoryCode)),
    ]),
    resolvers.resolveCollectionIdsByCode([
      ...new Set(
        results.flatMap((_r, i) => collectionsFor(i).map((c) => c.code)),
      ),
    ]),
    unitResolutionsState,
  ]);
  if (!unitState.ok) {
    throw unitState.error;
  }
  const unitResolutions = unitState.value;

  const classifiedUnique = uniqueLines.map((it, i): ClassifiedLine => {
    const r = results[i];
    const code = r?.categoryCode ?? null;
    const categoryId = code ? (categoryIds.get(code) ?? null) : null;
    const attrs = attributesFor(i);
    const isTrueLumpSum = attrs
      ? attrs.isTrueLumpSum
      : (r?.isTrueLumpSum ?? false);
    const unitId = it.unit ? (unitResolutions.get(it.unit)?.id ?? null) : null;
    const collections = collectionsFor(i).flatMap((c) => {
      const collectionId = collectionIds.get(c.code);
      return collectionId
        ? [
            {
              collectionId,
              code: c.code,
              sourceUom: it.unit ?? '',
              sourceUnitId: unitId,
              conversionFactor: c.factor,
            },
          ]
        : [];
    });
    return {
      categoryId,
      categoryCode: categoryId ? code : null,
      isTrueLumpSum,
      liquidAcPattern: r?.liquidAcPattern ?? null,
      unitId,
      collections,
    };
  });

  return uniqueIndexByLine.map((uniqueIndex) => {
    const classified = classifiedUnique[uniqueIndex];
    if (!classified) {
      throw new Error('Classification result is missing for an input item.');
    }
    return {
      ...classified,
      collections: classified.collections.map((collection) => ({
        ...collection,
      })),
    };
  });
}

function deduplicateLines(lines: ClassifyLine[]): {
  uniqueLines: ClassifyLine[];
  uniqueIndexByLine: number[];
} {
  const uniqueLines: ClassifyLine[] = [];
  const uniqueIndexByLine: number[] = [];
  const byDescription = new Map<string, Map<string | null, number>>();

  for (const line of lines) {
    let byUnit = byDescription.get(line.description);
    if (!byUnit) {
      byUnit = new Map<string | null, number>();
      byDescription.set(line.description, byUnit);
    }
    let uniqueIndex = byUnit.get(line.unit);
    if (uniqueIndex === undefined) {
      uniqueIndex = uniqueLines.length;
      uniqueLines.push(line);
      byUnit.set(line.unit, uniqueIndex);
    }
    uniqueIndexByLine.push(uniqueIndex);
  }

  return { uniqueLines, uniqueIndexByLine };
}

async function classifyInBatches(
  inputs: ClassifierInput[],
  model: LanguageModel,
  options: { costAccounting?: LlmCostAccounting; stage: string },
): Promise<Awaited<ReturnType<typeof classifyBidTabItems>>> {
  const batches: Array<{ offset: number; inputs: ClassifierInput[] }> = [];
  for (
    let offset = 0;
    offset < inputs.length;
    offset += CLASSIFICATION_BATCH_SIZE
  ) {
    batches.push({
      offset,
      inputs: inputs.slice(offset, offset + CLASSIFICATION_BATCH_SIZE),
    });
  }

  const results: Awaited<ReturnType<typeof classifyBidTabItems>> = new Array(
    inputs.length,
  );
  let nextBatch = 0;
  let failed = false;
  let firstError: unknown;

  const worker = async (): Promise<void> => {
    while (!failed) {
      const batchIndex = nextBatch;
      nextBatch += 1;
      const batch = batches[batchIndex];
      if (!batch) {
        return;
      }
      try {
        const classified = await classifyBatch(batch.inputs, model, options);
        if (classified.length !== batch.inputs.length) {
          throw new Error(
            `Classification returned ${classified.length} results for ${batch.inputs.length} items.`,
          );
        }
        classified.forEach((result, index) => {
          results[batch.offset + index] = result;
        });
      } catch (error) {
        if (!failed) {
          failed = true;
          firstError = error;
        }
      }
    }
  };

  await Promise.all(
    Array.from(
      { length: Math.min(CLASSIFICATION_CONCURRENCY, batches.length) },
      worker,
    ),
  );
  if (failed) {
    throw firstError;
  }
  return results;
}

async function classifyBatch(
  inputs: ClassifierInput[],
  model: LanguageModel,
  options: { costAccounting?: LlmCostAccounting; stage: string },
): Promise<Awaited<ReturnType<typeof classifyBidTabItems>>> {
  try {
    return await classifyBidTabItems(inputs, { model, ...options });
  } catch (error) {
    if (
      error instanceof Error &&
      error.name === 'ClassificationOutputTruncatedError' &&
      inputs.length > 1
    ) {
      const midpoint = Math.ceil(inputs.length / 2);
      const left = await classifyBatch(
        inputs.slice(0, midpoint),
        model,
        options,
      );
      const right = await classifyBatch(inputs.slice(midpoint), model, options);
      return [...left, ...right];
    }
    throw error;
  }
}

async function settle<T>(
  promise: Promise<T>,
): Promise<{ ok: true; value: T } | { ok: false; error: unknown }> {
  try {
    return { ok: true, value: await promise };
  } catch (error) {
    return { ok: false, error };
  }
}
